import type { DigitalCouponFacetQuery, OrderLineFragment } from '#graphql-operations'

/**
 * The in-flight (or settled) widening request, shared by every caller.
 *
 * Module-scoped rather than `useState` because it holds a Promise, which cannot
 * be serialized into the SSR payload — and never needs to be: the request is
 * client-only, so on the server this stays null.
 *
 * Memoised as the PROMISE, not as an "already started" boolean. `useCheckout`'s
 * `init()` awaits this to guarantee the value codes are known before it picks a
 * shipping method; a boolean would let it walk straight past a request that was
 * still in flight — started moments earlier by the cart drawer — and apply a paid
 * method to a coupon-only order. Every caller now awaits the same request.
 */
let facetResolution: Promise<void> | null = null

/**
 * "Every line in this order is a digital coupon" — the orders that are
 * delivered by email and must never be charged for delivery.
 *
 * The order line only carries a FacetValue's OWN code (the shared
 * `OrderLine` fragment selects `facetValues { id code }`, with no parent
 * `facet`), so the configured identifier is matched as a FacetValue code
 * first. If it turns out to name a *Facet* instead, `resolveDigitalCouponFacet`
 * widens it to that facet's value codes with a single lookup — much cheaper
 * than forking the shared fragment to add a `facet` join to every cart, order
 * and account-history payload site-wide.
 *
 * Reads the `$activeOrder` plugin ref rather than the Colada store: the store's
 * copy is a separate, staler snapshot during checkout (see the note in
 * `useShippingEstimate`), and the checkout page, `useCheckout` and the template
 * all work off the plugin ref.
 *
 * Deliberately independent of `useCheckout` so the cart drawer and the estimate
 * composables can consume it without dragging checkout's watchers along.
 */
export function useDigitalCouponOrder() {
  const { activeOrder } = useActiveOrder()
  const { digitalCouponFacetCode } = useRuntimeConfig().public

  const couponFacetValueCodes = useState<string[]>(
    'digital-coupon-facet-value-codes',
    () => (digitalCouponFacetCode ? [digitalCouponFacetCode] : []),
  )

  /**
   * One-shot widening of a Facet code into its FacetValue codes. Client-only
   * and fire-once — the answer is catalog structure, not per-visitor state.
   * Failure is non-fatal: the configured code stands as a FacetValue code,
   * which is the more likely reading anyway.
   */
  async function resolveDigitalCouponFacet() {
    if (!digitalCouponFacetCode || !import.meta.client)
      return
    facetResolution ??= (async () => {
      try {
        const { data } = await useGraphqlQuery('digitalCouponFacet', { code: digitalCouponFacetCode })
        const values: DigitalCouponFacetQuery['facets']['items'][number]['values'] | undefined
          = data.facets.items[0]?.values
        if (values?.length)
          couponFacetValueCodes.value = values.map(value => value.code)
      }
      catch (err) {
        console.error('Failed to resolve the digital-coupon facet', err)
      }
    })()
    return facetResolution
  }

  // Fire-and-forget for every consumer that never goes through
  // `useCheckout.init()` — the cart drawer's free-shipping bar
  // (`useFreeShippingProgress`) and the cart total's shipping estimate
  // (`useShippingEstimate`) both read `isDigitalOnlyOrder`. Without it, a
  // `digitalCouponFacetCode` that names a Facet rather than a FacetValue is never
  // widened for them, and only checkout recognises a coupon-only cart: the
  // backend matches either form and quotes the free pickup method, while the
  // drawer goes on advertising a courier fee that checkout then removes.
  // Deduped through `facetResolution`, so it is at most one request per visit.
  void resolveDigitalCouponFacet()

  function lineIsDigitalCoupon(line: OrderLineFragment) {
    return line.productVariant.product.facetValues.some(fv => couponFacetValueCodes.value.includes(fv.code))
  }

  /**
   * `[].every()` is `true`, so the length guard is load-bearing: without it an
   * empty or still-loading cart reads as digital-only and the shipping selector
   * would vanish for an ordinary order during the load window — and again the
   * moment a customer empties the cart in place.
   */
  const isDigitalOnlyOrder = computed(() => {
    const lines = activeOrder.value?.lines
    return !!lines?.length && lines.every(lineIsDigitalCoupon)
  })

  return { isDigitalOnlyOrder, couponFacetValueCodes, resolveDigitalCouponFacet }
}
